/**
 * Celestial themes ported from the iOS app (ThemeManager.swift). Every theme
 * is dark except Pulsar. The active theme is a data attribute on <html>;
 * src/styles/index.css maps it to the design tokens.
 */
export const THEMES = [
  'neptune',
  'mars',
  'sun',
  'uranus',
  'earth',
  'nebula',
  'blackHole',
  'pulsar',
] as const

export type Theme = (typeof THEMES)[number]

export const DEFAULT_THEME: Theme = 'neptune'

/** Keep in sync with the pre-paint script in index.html. */
const THEME_STORAGE_KEY = 'app.theme'

/**
 * Raw palette values for the theme picker cards, which must render each
 * theme's own colors regardless of the active theme. Values mirror
 * src/styles/index.css (single source of truth for actual rendering).
 */
export interface ThemePreview {
  background: string
  surface: string
  primary: string
  secondary: string
  onBackground: string
  muted: string
  outline: string
}

export const THEME_PREVIEWS: Record<Theme, ThemePreview> = {
  neptune: {
    background: '#070b1f',
    surface: '#0e142e',
    primary: '#3d7bff',
    secondary: '#7fa8ff',
    onBackground: '#ecf1ff',
    muted: '#6f7ea6',
    outline: '#1e2a4f',
  },
  mars: {
    background: '#180808',
    surface: '#220d0d',
    primary: '#d93a2b',
    secondary: '#e8836f',
    onBackground: '#ffeeec',
    muted: '#99706b',
    outline: '#4a221f',
  },
  sun: {
    background: '#171204',
    surface: '#221c0d',
    primary: '#d9a82b',
    secondary: '#e8cb6f',
    onBackground: '#fff8ec',
    muted: '#99906b',
    outline: '#4a3d1f',
  },
  uranus: {
    background: '#08180e',
    surface: '#0d2216',
    primary: '#2bb963',
    secondary: '#6fe89b',
    onBackground: '#ecfff2',
    muted: '#6b9980',
    outline: '#1f4a2e',
  },
  earth: {
    background: '#0e1512',
    surface: '#18211c',
    primary: '#a98e4e',
    secondary: '#cbb37d',
    onBackground: '#f2efe4',
    muted: '#8c917f',
    outline: '#2e3a31',
  },
  nebula: {
    background: '#160d06',
    surface: '#21140a',
    primary: '#e8722b',
    secondary: '#f0a46f',
    onBackground: '#fff3ea',
    muted: '#99816b',
    outline: '#4a311f',
  },
  blackHole: {
    background: '#000000',
    surface: '#101013',
    primary: '#8a8a93',
    secondary: '#c7c7ce',
    onBackground: '#f5f5f7',
    muted: '#6e6e76',
    outline: '#232326',
  },
  pulsar: {
    background: '#fafafc',
    surface: '#ffffff',
    primary: '#3d6fd9',
    secondary: '#86a8e8',
    onBackground: '#17171c',
    muted: '#73737c',
    outline: '#e3e3ea',
  },
}

export function getStoredTheme(): Theme {
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  return THEMES.includes(stored as Theme) ? (stored as Theme) : DEFAULT_THEME
}

export function storeTheme(theme: Theme): void {
  localStorage.setItem(THEME_STORAGE_KEY, theme)
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme
}
